import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, Images } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";
import { SITE } from "../lib/site";

const src = (u) => (u?.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + u : u);

/* My Photos — every travel photo the user has uploaded, grouped by trip.
   Each group links back to that trip's Photos area. */
export default function MyPhotos() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { document.title = `My Photos | ${SITE.name}`; }, []);
  useEffect(() => {
    if (user) api.get("/album/mine/all").then((r) => setData(r.data)).catch(() => setData({ groups: [], total: 0 }));
  }, [user]);

  if (authLoading || (user && !data)) return <Spinner full />;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="my-photos-page">
      <Link to="/travel-hub" className="inline-flex items-center gap-1.5 text-sm text-blue-700 font-700 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to My Travel Hub
      </Link>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl text-emerald-700 flex items-center gap-2.5">
        <Images className="w-8 h-8" /> My Photos
        {data && <span className="text-base font-mono text-muted-foreground">({data.total})</span>}
      </h1>
      <p className="text-sm text-muted-foreground mt-1 mb-8">
        All your travel photos in one place, grouped by trip. Add more from any trip page — open the trip and use <b>Add photos</b> in its Photos area.
      </p>

      {data?.groups?.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-10 text-center" data-testid="my-photos-empty">
          <Images className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-muted-foreground">No photos yet. Open a trip in <Link to="/travel-hub" className="text-blue-700 font-700 hover:underline">My Travel Hub</Link> and tap <b>Add photos</b> to start your collection.</p>
        </div>
      ) : (
        <div className="space-y-10">
          {data.groups.map((g) => (
            <section key={g.trip_id} data-testid={`photos-group-${g.trip_id}`}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
                <Link to={`/trips/${g.trip_id}#photos`} data-testid={`photos-group-title-${g.trip_id}`}
                  className="font-heading font-900 text-xl text-zinc-800 hover:text-emerald-700 transition-colors">
                  {g.title}
                </Link>
                {(g.start_date || g.end_date) && (
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" /> {g.start_date}{g.end_date ? ` → ${g.end_date}` : ""}
                  </span>
                )}
                <span className="text-xs font-mono text-emerald-700">{g.count} photo{g.count !== 1 ? "s" : ""}</span>
                <Link to={`/trips/${g.trip_id}#photos`} className="text-xs text-blue-700 font-700 hover:underline ml-auto">
                  Open trip photos →
                </Link>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {g.photos.map((p) => (
                  <Link key={p.id} to={`/trips/${g.trip_id}#photos`} title={p.title || p.location || ""}>
                    <img src={src(p.url)} alt={p.title || ""} loading="lazy"
                      className="w-full aspect-square object-cover border border-zinc-200 hover:opacity-90 transition-opacity" />
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
